import styled, { createGlobalStyle } from 'styled-components';

/* ======================================================
   THEME (same as the Home page / OptiiiPage)
   Font: Poppins comes from the app's global index.css (`*` rule).
====================================================== */

export const theme = {
  bg: '#07070A',
  bgAlt: 'rgba(255,255,255,0.03)',
  card: 'rgba(255,255,255,0.06)',
  text: '#F5F5F7',
  subText: 'rgba(245,245,247,0.72)',
  accent: '#7C3AED', // purple
  border: 'rgba(255,255,255,0.12)',
};

export const GlobalStyle = createGlobalStyle`
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  html, body { height: 100%; }
  body {
    margin: 0;
    background: #000;
    color: ${theme.text};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  @media print {
    html, body { overflow: visible !important; background: ${theme.bg}; }
    @page { size: 1920px 1080px; margin: 0; }
  }
`;

/* The site's hero gradient, scaled for a 1920px canvas */
const heroBg = (w, h) => `
  radial-gradient(${w}px ${h}px at 18% 30%, ${theme.accent}28, transparent 55%),
  radial-gradient(${w}px ${h}px at 78% 70%, rgba(59,130,246,0.18), transparent 55%),
  linear-gradient(180deg, rgba(255,255,255,0.03), transparent 28%)
`;

/* ---------- Stage ---------- */

export const Viewport = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media print {
    position: static;
    display: block;
  }
`;

export const Stage = styled.div`
  position: relative;
  width: 1920px;
  height: 1080px;
  flex: none;
  overflow: hidden;
  transform-origin: center center;
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text};

  @media print {
    transform: none !important;
    height: auto;
    overflow: visible;
  }

  /* --- Slide frame --- */
  .slide {
    position: absolute;
    inset: 0;
    padding: 150px 140px;
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.bg};
    opacity: 0;
    visibility: hidden;
    transform: translateY(24px);
    transition: opacity 0.55s ease, transform 0.55s ease, visibility 0.55s;

    @media print {
      position: relative;
      opacity: 1;
      visibility: visible;
      transform: none;
      height: 1080px;
      page-break-after: always;
      transition: none;
    }
  }
  .slide.active { opacity: 1; visibility: visible; transform: none; }
  .slide > .slideBody {
    position: absolute; inset: 0; padding: 150px 140px;
    display: flex; flex-direction: column;
    transform: scale(0.86); transform-origin: 50% 46%;
  }
  .slide > .slideBody.center { justify-content: center; }
  .slide > .footnote {
    position: absolute; left: 140px; right: 140px; bottom: 100px;
    font-size: 15px; color: rgba(245,245,247,0.45); line-height: 1.4;
  }
  .slide.alt { background: linear-gradient(${({ theme }) => theme.bgAlt}, ${({ theme }) => theme.bgAlt}), ${({ theme }) => theme.bg}; }
  .slide.center { justify-content: center; }
  .slide > * { position: relative; }
  .slide > .heroBg { position: absolute; inset: 0; pointer-events: none; background: ${heroBg(1500, 680)}; }

  /* --- Pill top bar (mirrors the site nav) --- */
  .topbar {
    position: absolute;
    top: 44px; left: 140px; right: 140px;
    display: flex; justify-content: space-between; align-items: center;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(14px);
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 999px;
    padding: 14px 18px 14px 26px;
  }
  .brand { display: inline-flex; align-items: center; gap: 12px; font-weight: 700; letter-spacing: 0.4px; font-size: 26px; }
  .tag {
    font-size: 20px; color: ${({ theme }) => theme.subText};
    padding: 10px 20px; border-radius: 999px;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
  }

  /* --- Type --- */
  .eyebrow {
    display: inline-flex; align-items: center; gap: 14px;
    font-size: 22px; text-transform: uppercase; letter-spacing: 1.8px;
    color: ${({ theme }) => theme.subText}; margin-bottom: 26px;
    svg { color: ${({ theme }) => theme.accent}; }
  }
  h1 { font-size: 96px; font-weight: 520; line-height: 1.06; letter-spacing: -0.5px; margin: 0; }
  h2 { font-size: 64px; font-weight: 520; line-height: 1.12; margin: 0 0 22px; }
  h1 em, h2 em { font-style: italic; font-weight: 650; }
  h3 { font-size: 30px; font-weight: 600; line-height: 1.3; margin: 0; }
  .sub { font-size: 28px; line-height: 1.65; color: ${({ theme }) => theme.subText}; max-width: 1400px; margin: 0; }
  .sub strong { color: ${({ theme }) => theme.text}; font-weight: 600; }
  .head { margin-bottom: 56px; }

  /* --- Cards & components (scaled from the site) --- */
  .grid { display: grid; gap: 28px; }
  .g2 { grid-template-columns: 1fr 1fr; }
  .g3 { grid-template-columns: repeat(3, 1fr); }
  .g4 { grid-template-columns: repeat(4, 1fr); }
  .card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 40px; padding: 44px;
    p { color: ${({ theme }) => theme.subText}; font-size: 24px; line-height: 1.65; margin: 0; }
    h3 { margin: 28px 0 14px; }
  }
  .card.row { display: flex; gap: 30px; padding: 40px; h3 { margin: 0 0 10px; } }
  .soft {
    background: rgba(0,0,0,0.35);
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 40px; padding: 44px;
  }
  .soft.raised {
    background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05));
    box-shadow: 0 40px 80px rgba(0,0,0,0.45);
  }
  .iconWrap {
    width: 76px; height: 76px; border-radius: 24px; flex: none;
    background: ${({ theme }) => `${theme.accent}22`};
    color: ${({ theme }) => theme.accent};
    display: flex; align-items: center; justify-content: center;
  }
  .badges { display: flex; flex-wrap: wrap; gap: 16px; }
  .badge {
    display: inline-flex; align-items: center; gap: 14px;
    padding: 16px 22px; border-radius: 999px;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
    color: rgba(245,245,247,0.9); font-size: 22px;
  }
  .badge.square { border-radius: 24px; svg { color: ${({ theme }) => theme.accent}; } }
  .btn {
    display: inline-flex; align-items: center; gap: 14px;
    padding: 22px 32px; border-radius: 999px;
    font-size: 24px; font-weight: 700;
    background: ${({ theme }) => theme.accent}; color: #fff;
  }
  .btn2 {
    display: inline-flex; align-items: center; gap: 14px;
    padding: 22px 32px; border-radius: 999px;
    font-size: 24px; font-weight: 650;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
  }
  .checks { display: grid; gap: 16px; }
  .check {
    display: flex; gap: 16px; align-items: flex-start;
    color: rgba(245,245,247,0.86); font-size: 24px; line-height: 1.5;
    svg { color: ${({ theme }) => theme.accent}; margin-top: 4px; flex: none; }
  }
  .resultNum { font-size: 64px; font-weight: 900; letter-spacing: -1px; color: ${({ theme }) => theme.accent}; line-height: 1.05; margin-bottom: 12px; }
  .resultLabel { font-size: 26px; font-weight: 700; margin-bottom: 10px; line-height: 1.35; }
  .resultDetail { font-size: 22px; line-height: 1.6; color: ${({ theme }) => theme.subText}; }
  .note { font-size: 20px; color: ${({ theme }) => theme.subText}; line-height: 1.6; margin: 0; }
  .callout {
    background: rgba(255,255,255,0.06);
    border-left: 6px solid ${({ theme }) => theme.accent};
    padding: 28px 32px; border-radius: 24px;
    font-size: 26px; line-height: 1.6;
  }
  .trust { font-size: 22px; color: ${({ theme }) => theme.subText}; line-height: 1.6; margin: 0; }
  .trust strong { color: rgba(245,245,247,0.92); font-weight: 600; }
  .ph {
    color: #C4B5FD; background: rgba(124,58,237,0.16);
    border: 1px dashed rgba(196,181,253,0.5); border-radius: 10px; padding: 0 10px;
  }

  /* --- Opportunity rows --- */
  .oppHead, .opp { display: grid; grid-template-columns: 76px 1fr 1.25fr 0.95fr; gap: 32px; }
  .oppHead { padding: 0 36px 6px; font-size: 18px; text-transform: uppercase; letter-spacing: 1.6px; color: rgba(245,245,247,0.52); }
  .opp {
    align-items: center; padding: 18px 32px; border-radius: 28px;
    background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border};
    .iconWrap { width: 64px; height: 64px; border-radius: 20px; }
    b { font-size: 26px; font-weight: 600; }
    span { font-size: 22px; color: ${({ theme }) => theme.subText}; line-height: 1.5; }
    i { font-style: normal; font-size: 22px; color: rgba(245,245,247,0.86); display: flex; gap: 12px; align-items: center; }
    i svg { color: ${({ theme }) => theme.accent}; flex: none; }
  }

  /* --- Engagement model cards --- */
  .proc {
    background: rgba(255,255,255,0.05); border: 1px solid ${({ theme }) => theme.border};
    border-radius: 40px; padding: 34px;
    display: flex; flex-direction: column; gap: 20px;
    .check { font-size: 22px; }
  }
  .procTop { display: flex; gap: 24px; align-items: flex-start; }
  .procTitle { font-size: 28px; font-weight: 700; line-height: 1.3; }
  .procText { font-size: 21px; color: ${({ theme }) => theme.subText}; line-height: 1.6; }

  /* --- Response capture --- */
  .choice { display: flex; flex-direction: column; gap: 16px; }
  .choiceQ {
    display: flex; justify-content: space-between; align-items: baseline; gap: 24px;
    font-size: 26px; font-weight: 600;
  }
  .choiceHint { font-size: 18px; font-weight: 400; color: rgba(245,245,247,0.52); text-transform: uppercase; letter-spacing: 1.4px; white-space: nowrap; }
  .choiceOpts { display: flex; flex-wrap: wrap; gap: 12px; }
  .opt {
    display: inline-flex; align-items: center; gap: 12px;
    padding: 14px 22px; border-radius: 999px; cursor: pointer;
    font-size: 22px; color: rgba(245,245,247,0.9); text-align: left;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
    transition: background 0.15s, border-color 0.15s;
    svg { color: rgba(245,245,247,0.4); flex: none; }
    &:hover { background: rgba(255,255,255,0.1); }
    &:focus-visible { outline: 2px solid ${({ theme }) => theme.accent}; outline-offset: 3px; }
  }
  .opt.on {
    background: ${({ theme }) => `${theme.accent}33`}; border-color: ${({ theme }) => theme.accent}; color: #fff;
    svg { color: #C4B5FD; }
  }
  .choice.compact .opt { padding: 12px 18px; font-size: 20px; }
  .choice.compact .choiceQ { font-size: 24px; }

  /* Whole cards that can be selected */
  .selectable { cursor: pointer; transition: background 0.15s, border-color 0.15s; position: relative; }
  .selectable:hover { background: rgba(255,255,255,0.09); }
  .selectable:focus-visible { outline: 2px solid ${({ theme }) => theme.accent}; outline-offset: 3px; }
  .selectable.on { background: ${({ theme }) => `${theme.accent}26`}; border-color: ${({ theme }) => theme.accent}; }
  .pick {
    position: absolute; top: 24px; right: 24px;
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 16px; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,245,247,0.45);
  }
  .on .pick { color: #C4B5FD; }
  .prompt {
    display: inline-flex; align-items: center; gap: 12px;
    font-size: 20px; color: #C4B5FD; letter-spacing: 0.4px;
  }

  /* Summary + send */
  .summary { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 28px; align-items: start; }
  .summaryList {
    background: rgba(0,0,0,0.35); border: 1px solid ${({ theme }) => theme.border};
    border-radius: 40px; padding: 14px 32px 18px;
    display: grid; grid-template-columns: 1fr 1fr; column-gap: 32px;
  }
  .summaryRow { display: flex; flex-direction: column; gap: 2px; padding: 12px 0; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  .summaryQ { font-size: 16px; text-transform: uppercase; letter-spacing: 1.1px; color: ${({ theme }) => theme.subText}; }
  .summaryA { font-size: 20px; font-weight: 600; }
  .summaryA.empty { font-weight: 400; color: rgba(245,245,247,0.4); font-style: italic; }
  .summaryMeta { grid-column: 1 / -1; display: flex; justify-content: space-between; padding-top: 16px; font-size: 18px; color: rgba(245,245,247,0.52); }
  .linkBtn { background: none; border: none; color: #C4B5FD; font-size: 18px; cursor: pointer; text-decoration: underline; }
  .summaryForm {
    background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05));
    border: 1px solid ${({ theme }) => theme.border}; border-radius: 40px; padding: 36px;
    box-shadow: 0 40px 80px rgba(0,0,0,0.45);
    display: flex; flex-direction: column; gap: 16px;
    h3 { margin-bottom: 4px; }
  }
  .field { display: flex; flex-direction: column; gap: 8px; }
  .field label { font-size: 18px; color: rgba(245,245,247,0.72); }
  .field input, .field textarea {
    background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.16);
    border-radius: 18px; padding: 14px 18px; color: #fff; font-size: 21px; line-height: 1.5; resize: none;
    &::placeholder { color: rgba(245,245,247,0.45); }
    &:focus { outline: none; border-color: rgba(255,255,255,0.32); }
  }
  .submit {
    margin-top: 6px; background: #fff; color: #000; border: none; border-radius: 999px;
    padding: 18px 20px; font-size: 22px; font-weight: 800; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 12px;
    &:disabled { opacity: 0.6; cursor: default; }
  }
  .summaryList.full { grid-template-columns: repeat(3, 1fr); column-gap: 40px; padding: 18px 40px 22px; }
  .summaryList.full .summaryQ { font-size: 16px; }
  .summaryList.full .summaryA { font-size: 22px; }
  .summaryMeta span { display: inline-flex; align-items: center; gap: 8px; }
  .saveStatus { display: inline-flex; align-items: center; gap: 6px; }
  .saveStatus.saved, .saveStatus.local { color: #86EFAC; }
  .saveStatus.error { color: #FCD34D; }
  .formMsg { font-size: 18px; min-height: 26px; }
  .formMsg.sent { color: rgba(167,243,208,0.95); }
  .formMsg.error { color: rgba(252,165,165,0.95); }

  /* People */
  .avatar {
    width: 96px; height: 96px; border-radius: 999px; flex: none;
    display: flex; align-items: center; justify-content: center;
    font-size: 34px; font-weight: 700; color: #fff;
    background: linear-gradient(135deg, ${({ theme }) => theme.accent}, rgba(59,130,246,0.8));
  }
  .role { font-size: 22px; color: #C4B5FD; margin-top: 6px; }

  .teamCard { display: flex; flex-direction: column; gap: 22px; padding: 40px; }
  .teamCard h3 { margin: 0; font-size: 29px; white-space: nowrap; }
  .teamCard .role { margin-top: 4px; }
  .teamCard .check { font-size: 21px; }
  .teamCard .checks { gap: 14px; }
  .teamHead { display: flex; gap: 24px; align-items: center; }

  /* Logo slots, tiles and focus choice */
  .logoSlot { display: inline-flex; align-items: center; gap: 12px; }
  .logoImg { display: block; flex: none; object-fit: contain; border-radius: 8px; background: rgba(255,255,255,0.92); padding: 3px; }
  .logoText { font-weight: 600; letter-spacing: 0.2px; }
  .opt .logoText { font-weight: 400; }
  .tile .logoSlot { flex-direction: column; gap: 12px; }
  .tiles { display: grid; gap: 16px; }
  .tile {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    min-height: 120px; padding: 20px; border-radius: 28px; cursor: pointer;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
    color: #fff; font-size: 26px; text-align: center;
    transition: background 0.15s, border-color 0.15s, transform 0.15s;
    &:hover { background: rgba(255,255,255,0.1); transform: translateY(-2px); }
    &:focus-visible { outline: 2px solid ${({ theme }) => theme.accent}; outline-offset: 3px; }
  }
  .tile.on { background: ${({ theme }) => `${theme.accent}33`}; border-color: ${({ theme }) => theme.accent}; }
  .tile.mini { min-height: 0; padding: 10px 20px; border-radius: 999px; font-size: 22px; cursor: default; svg { color: #C4B5FD; } }
  .tile.mini:hover { transform: none; }
  .focusPick { display: flex; flex-direction: column; gap: 22px; }
  .focusOpen { display: flex; flex-direction: column; gap: 26px; animation: focusIn 0.45s ease; }
  @keyframes focusIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { .focusOpen { animation: none; } }
  .focusBar { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
  .focusLabel { font-size: 18px; text-transform: uppercase; letter-spacing: 1.6px; color: rgba(245,245,247,0.52); }
  .changeBtn {
    display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
    background: none; border: none; color: #C4B5FD; font-size: 20px; padding: 8px 10px; border-radius: 999px;
    &:hover { background: rgba(255,255,255,0.06); }
  }
  .segWrap { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .segmented {
    display: inline-flex; padding: 6px; gap: 4px; border-radius: 999px;
    background: rgba(0,0,0,0.35); border: 1px solid ${({ theme }) => theme.border};
    button {
      border: none; background: transparent; cursor: pointer; color: ${({ theme }) => theme.subText};
      font-size: 20px; padding: 10px 20px; border-radius: 999px;
      &:hover { color: #fff; }
    }
    button.on { background: ${({ theme }) => theme.accent}; color: #fff; font-weight: 600; }
  }

  /* Data-flow diagram */
  .flow { position: relative; width: 1640px; height: 336px; }
  .flow svg.wires { position: absolute; inset: 0; overflow: visible; }
  .flow .wire { fill: none; stroke: rgba(124,58,237,0.55); stroke-width: 2.5; stroke-dasharray: 8 8; animation: flowDash 1.4s linear infinite; }
  @keyframes flowDash { to { stroke-dashoffset: -32; } }
  @media (prefers-reduced-motion: reduce) { .flow .wire { animation: none; } }
  .node {
    position: absolute; display: flex; flex-direction: column; justify-content: center; gap: 4px;
    padding: 12px 22px; border-radius: 22px;
    background: rgba(255,255,255,0.06); border: 1px solid ${({ theme }) => theme.border};
  }
  .node b { font-size: 22px; font-weight: 600; display: flex; align-items: center; gap: 12px; }
  .node b svg { color: ${({ theme }) => theme.accent}; flex: none; }
  .node span { font-size: 17px; color: ${({ theme }) => theme.subText}; line-height: 1.35; }
  .node.hl { background: ${({ theme }) => `${theme.accent}26`}; border-color: ${({ theme }) => theme.accent}; }
  .node.layer {
    background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05));
    box-shadow: 0 30px 60px rgba(0,0,0,0.45); justify-content: flex-start; gap: 10px; padding: 20px 24px;
  }
  .layerChips { display: grid; gap: 8px; }
  .layerChip { font-size: 18px; padding: 8px 14px; border-radius: 14px; background: rgba(0,0,0,0.3); border: 1px solid ${({ theme }) => theme.border}; }
  .layerFoot { margin-top: auto; font-size: 16px; color: #C4B5FD; letter-spacing: 0.4px; display: flex; gap: 8px; align-items: center; }
  .notes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .noteCard { padding: 18px 24px; border-radius: 24px; background: rgba(0,0,0,0.35); border: 1px solid ${({ theme }) => theme.border}; }
  .noteCard .dayLabel { margin-bottom: 8px; }
  .noteCard p { margin: 0; font-size: 18px; line-height: 1.5; color: ${({ theme }) => theme.subText}; }

  .tile.mini .logoSlot { flex-direction: row; }
  .noteCard .dayLabel { display: flex; align-items: center; gap: 8px; }
  .noteCard b { color: #fff; font-weight: 600; }

  /* Close timeline */
  .tl { display: grid; gap: 10px; }
  .tlHead { display: flex; justify-content: space-between; align-items: center; gap: 24px; }
  .tlHead b { font-size: 24px; font-weight: 600; }
  .tlTag { font-size: 18px; font-weight: 600; padding: 6px 16px; border-radius: 999px; border: 1px solid ${({ theme }) => theme.border}; }
  .tlTag.warn { color: #FCD34D; background: rgba(252,211,77,0.08); border-color: rgba(252,211,77,0.35); }
  .tlTag.good { color: #86EFAC; background: rgba(134,239,172,0.08); border-color: rgba(134,239,172,0.35); }
  .tlBar { display: flex; height: 44px; border-radius: 14px; overflow: hidden; border: 1px solid ${({ theme }) => theme.border}; }
  .tlSeg {
    flex: none; display: flex; align-items: center; justify-content: center;
    font-size: 18px; font-weight: 700; white-space: nowrap; overflow: hidden; color: #fff;
    transition: width 0.5s ease;
  }
  .tlSeg + .tlSeg { border-left: 2px solid ${({ theme }) => theme.bg}; }
  .tlSeg.softClose, .dot.softClose { background: rgba(255,255,255,0.28); }
  .tlSeg.hardClose, .dot.hardClose { background: rgba(255,255,255,0.5); }
  .tlSeg.audit, .dot.audit { background: ${({ theme }) => theme.accent}; }
  .tlSeg.board, .dot.board { background: #3B82F6; }
  .tlSeg.ir, .dot.ir { background: #0EA5A4; }
  .tlSteps { display: grid; grid-template-columns: 1fr 1fr 1.35fr 0.95fr 1fr; gap: 14px; }
  .tlStep { display: flex; gap: 10px; align-items: flex-start; }
  .dot { width: 12px; height: 12px; border-radius: 4px; flex: none; margin-top: 7px; }
  .tlStepName { font-size: 17px; color: ${({ theme }) => theme.subText}; line-height: 1.35; }
  .tlStepMeta { font-size: 18px; color: rgba(245,245,247,0.86); }
  .tlStepMeta b { color: #fff; font-weight: 700; }
  .tlStepMeta .plus { color: #86EFAC; font-weight: 700; }
  @media (prefers-reduced-motion: reduce) { .tlSeg { transition: none; } }
  .relSlider { display: flex; align-items: center; gap: 12px; padding: 8px 14px; border-radius: 999px; background: rgba(0,0,0,0.35); border: 1px solid ${({ theme }) => theme.border}; }
  .relSlider label { font-size: 19px; color: ${({ theme }) => theme.subText}; white-space: nowrap; }
  .relSlider label b { color: #fff; font-weight: 700; }
  .relSlider .plus { color: #86EFAC; font-weight: 600; }
  .relSlider input[type='range'] { width: 170px; accent-color: ${({ theme }) => theme.accent}; cursor: grab; }
  .relSlider input[type='range']:active { cursor: grabbing; }
  .relSlider .changeBtn { font-size: 17px; padding: 4px 8px; }

  /* Engagement timeline (gantt) */
  .gantt { display: grid; gap: 6px; }
  .ganttRow { display: grid; grid-template-columns: 700px 1fr; gap: 36px; align-items: center; }
  .ganttRow + .ganttRow:not(.ganttAxisRow) { }
  .ganttLabel { display: flex; gap: 18px; align-items: flex-start; }
  .ganttLabel .iconWrap { width: 52px !important; height: 52px !important; }
  .ganttTitle { font-size: 24px; font-weight: 600; display: flex; align-items: center; gap: 12px; }
  .ganttNum {
    width: 30px; height: 30px; border-radius: 999px; flex: none;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 16px; font-weight: 700; color: #fff; background: ${({ theme }) => theme.accent};
  }
  .ganttWhen { font-size: 17px; color: #C4B5FD; margin-top: 0; }
  .ganttText { font-size: 18px; line-height: 1.4; color: ${({ theme }) => theme.subText}; margin-top: 4px; }
  .ganttAxis { position: relative; height: 26px; }
  .ganttAxis span {
    position: absolute; top: 0; transform: translateX(-50%); white-space: nowrap;
    font-size: 17px; color: rgba(245,245,247,0.45);
  }
  .ganttAxis span:first-child { transform: none; }
  .ganttAxis span.key { color: #fff; font-weight: 600; }
  .ganttTrack { position: relative; height: 56px; }
  .ganttGrid {
    position: absolute; inset: 0; border-radius: 14px;
    background:
      repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 1px, transparent 1px calc(100% / 9)),
      rgba(255,255,255,0.025);
  }
  .ganttBar { position: absolute; top: 8px; bottom: 8px; border-radius: 12px; }
  .ganttBar.s1 { background: rgba(255,255,255,0.55); }
  .ganttBar.s2 { background: #3B82F6; }
  .ganttBar.s3 { background: ${({ theme }) => theme.accent}; }
  .ganttBar.s4 { background: linear-gradient(90deg, #0EA5A4, rgba(14,165,164,0)); }
  .ganttBar.open { border-top-right-radius: 0; border-bottom-right-radius: 0; }
  .ganttBar.training { top: 18px; bottom: 18px; background: rgba(196,181,253,0.55); border-radius: 999px; }
  .ganttBar.training.open { background: repeating-linear-gradient(90deg, rgba(196,181,253,0.45) 0 14px, transparent 14px 24px); }
  .ganttMilestone { position: absolute; top: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; }
  .ganttMilestone .diamond { width: 24px; height: 24px; background: #fff; transform: rotate(45deg); border-radius: 4px; box-shadow: 0 0 0 4px ${({ theme }) => theme.bg}; }
  .ganttMilestone .mLabel {
    position: absolute; left: 26px; white-space: nowrap; font-size: 17px; font-weight: 700;
    padding: 4px 12px; border-radius: 999px; background: #fff; color: #000;
  }
  .ganttTraining { margin-top: 4px; padding-top: 12px; border-top: 1px solid ${({ theme }) => theme.border}; }

  /* Org chart */
  .org { position: relative; width: 1640px; height: 640px; }
  .org > * { position: absolute; }
  .orgWires { left: 0; top: 0; overflow: visible; }
  .orgWires path { fill: none; stroke: rgba(196,181,253,0.7); stroke-width: 2.5; }
  .orgCard {
    display: flex; align-items: center; gap: 26px; padding: 20px 30px;
    background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border}; border-radius: 28px;
  }
  .orgCard.oversight {
    background: linear-gradient(90deg, ${({ theme }) => `${theme.accent}30`}, rgba(59,130,246,0.14));
    border-color: ${({ theme }) => `${theme.accent}99`};
  }
  .orgRole { font-size: 17px; text-transform: uppercase; letter-spacing: 1.4px; color: #C4B5FD; }
  .orgName { font-size: 30px; font-weight: 600; margin: 2px 0 6px; display: flex; align-items: baseline; gap: 16px; }
  .orgName span { font-size: 20px; font-weight: 400; color: ${({ theme }) => theme.subText}; }
  .orgFacts { font-size: 19px; line-height: 1.45; color: ${({ theme }) => theme.subText}; }
  .orgFacts .logoSlot { gap: 6px; vertical-align: middle; color: #fff; }
  .orgSide { margin-left: auto; display: flex; flex-direction: column; gap: 10px; align-items: flex-end; flex: none; }
  .orgTeam { border: 2px dashed rgba(196,181,253,0.4); border-radius: 36px; }
  .orgTeamLabel {
    position: absolute; top: -16px; left: 28px; padding: 2px 14px; background: ${({ theme }) => theme.bg};
    font-size: 17px; text-transform: uppercase; letter-spacing: 1.4px; color: rgba(245,245,247,0.7);
    display: inline-flex; align-items: center; gap: 10px;
  }
  .orgClient {
    padding: 28px 30px; border-radius: 36px;
    background: rgba(0,0,0,0.35); border: 1px solid ${({ theme }) => theme.border};
    display: flex; flex-direction: column; gap: 14px;
  }
  .orgClientRow { padding: 14px 20px; border-radius: 20px; background: rgba(255,255,255,0.05); border: 1px solid ${({ theme }) => theme.border}; }
  .orgClientRow b { display: block; font-size: 22px; font-weight: 600; }
  .orgClientRow span { font-size: 18px; color: ${({ theme }) => theme.subText}; }
  .wireLabel { font-size: 16px; color: #C4B5FD; white-space: nowrap; }
  .orgAdvisor {
    display: flex; align-items: center; gap: 22px; padding: 0 28px; border-radius: 22px;
    border: 1px solid ${({ theme }) => theme.border}; background: rgba(255,255,255,0.02);
  }
  .orgAdvisor b { font-size: 22px; font-weight: 600; }
  .orgAdvisor > span:last-child { font-size: 18px; color: ${({ theme }) => theme.subText}; }
  .orgCard.stack { flex-direction: column; align-items: stretch; justify-content: center; gap: 16px; }
  .orgSideCard {
    display: flex; flex-direction: column; gap: 22px; padding: 30px;
    border-radius: 36px; border: 1px solid ${({ theme }) => `${theme.accent}99`};
    background: linear-gradient(180deg, ${({ theme }) => `${theme.accent}30`}, rgba(59,130,246,0.10));
  }
  .orgList { display: grid; gap: 18px; }
  .orgList > div { display: flex; flex-direction: column; gap: 6px; }
  .orgList b { font-size: 16px; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,245,247,0.6); font-weight: 500; }
  .orgList span { font-size: 21px; color: #fff; }
  .orgList .logoSlot { gap: 8px; vertical-align: middle; }
  .orgChips { display: flex; flex-wrap: wrap; gap: 10px; }
  .orgChips .badge { padding: 10px 16px; font-size: 19px; }
  .orgNote { margin-top: auto; font-size: 18px; line-height: 1.5; color: #C4B5FD; padding-top: 18px; border-top: 1px solid rgba(196,181,253,0.25); }

  /* Buy / build */
  .path { display: flex; flex-direction: column; gap: 18px; }
  .path .checks { gap: 12px; }
  .path .check { font-size: 22px; }

  /* Timeline (day-by-day) */
  .days { display: grid; grid-template-columns: repeat(5, 1fr); gap: 20px; }
  .day {
    background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border};
    border-radius: 32px; padding: 30px; display: flex; flex-direction: column; gap: 12px;
  }
  .dayLabel { font-size: 18px; text-transform: uppercase; letter-spacing: 1.6px; color: #C4B5FD; }
  .day h3 { font-size: 26px; }
  .day p { font-size: 20px; line-height: 1.55; color: ${({ theme }) => theme.subText}; margin: 0; }
  .who { font-size: 18px; color: rgba(245,245,247,0.6); display: flex; gap: 8px; align-items: center; margin-top: auto; }

  .footBrand { display: inline-flex; align-items: center; gap: 16px; }
  .footRight { display: inline-flex; align-items: center; gap: 20px; }
  .footClient { display: inline-flex; align-items: center; gap: 10px; padding-right: 20px; border-right: 1px solid ${({ theme }) => theme.border}; color: rgba(245,245,247,0.6); font-size: 15px; font-weight: 600; }
  .clientLogo { display: block; flex: none; object-fit: contain; border-radius: 5px; background: #fff; padding: 2px; }
  .footSection { font-size: 13px; text-transform: uppercase; letter-spacing: 1.4px; color: rgba(245,245,247,0.4); padding-left: 16px; border-left: 1px solid ${({ theme }) => theme.border}; }

  /* Agenda */
  .agenda { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
  .agendaItem { display: flex; flex-direction: column; gap: 12px; padding: 30px; border-radius: 32px; background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border}; }
  .agendaNum { font-size: 56px; font-weight: 800; color: ${({ theme }) => theme.accent}; line-height: 1; }
  .agendaItem h3 { font-size: 28px; }
  .agendaItem p { font-size: 20px; line-height: 1.5; color: ${({ theme }) => theme.subText}; margin: 0; }
  .agendaItem.discussion { background: transparent; border-style: dashed; }
  .agendaItem.readAfter { background: transparent; border-style: dashed; border-color: rgba(245,245,247,0.16); }
  .agendaItem.readAfter .agendaNum { color: rgba(245,245,247,0.45); }
  .agendaTime { font-size: 18px; color: rgba(245,245,247,0.55); font-variant-numeric: tabular-nums; }

  /* Appendix contents */
  .appendixList { display: grid; gap: 0; border-top: 1px solid ${({ theme }) => theme.border}; }
  .appendixRow { display: grid; grid-template-columns: 64px 1fr auto; gap: 20px; align-items: baseline; padding: 13px 0; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  .appendixNum { font-size: 20px; font-weight: 700; color: ${({ theme }) => theme.accent}; font-variant-numeric: tabular-nums; }
  .appendixLabel { font-size: 23px; font-weight: 500; }
  .appendixSection { font-size: 16px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(245,245,247,0.5); white-space: nowrap; }

  /* Personal plan */
  .plan { display: grid; gap: 0; position: relative; padding-left: 8px; }
  .planRow { display: grid; grid-template-columns: 150px 1fr; gap: 22px; align-items: center; padding: 10px 0; position: relative; }
  .planDate { font-size: 19px; color: ${({ theme }) => theme.subText}; text-align: right; }
  .planWhat { display: flex; align-items: center; gap: 14px; font-size: 22px; }
  .planDot { width: 14px; height: 14px; border-radius: 999px; flex: none; }
  .planRow.ours .planDot { background: ${({ theme }) => theme.accent}; box-shadow: 0 0 0 4px ${({ theme }) => `${theme.accent}40`}; }
  .planRow.theirs .planDot { background: transparent; border: 2px solid #3B82F6; }
  .planRow.theirs .planWhat { color: #93C5FD; }
  .planRow.ours .planWhat { font-weight: 600; }
  .planTag { font-size: 14px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
  .planTag.fixed { color: #C4B5FD; background: ${({ theme }) => `${theme.accent}26`}; border: 1px solid ${({ theme }) => `${theme.accent}66`}; }
  .planTag.quoted { color: ${({ theme }) => theme.subText}; border: 1px dashed rgba(245,245,247,0.28); }
  .planRow.later .planWhat { color: rgba(245,245,247,0.72); }
  .scopeNote { margin-top: 22px; display: flex; gap: 14px; align-items: flex-start; font-size: 19px; line-height: 1.45; color: ${({ theme }) => theme.subText}; max-width: 860px; }
  .scopeNote b { color: #F5F5F7; font-weight: 600; }

  /* Advice */
  .advice {
    display: flex; align-items: flex-start; gap: 20px; padding: 18px 26px; border-radius: 22px;
    background: rgba(253,230,138,0.05);
    border: 1px solid rgba(253,230,138,0.22);
    border-left: 4px solid rgba(253,230,138,0.7);
  }
  .adviceTag {
    flex: none; display: inline-flex; align-items: center; gap: 8px; margin-top: 3px;
    font-size: 16px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #FDE68A; white-space: nowrap;
  }
  .adviceText { font-size: 21px; line-height: 1.5; color: rgba(245,245,247,0.92); }
  .adviceText b { color: #fff; }
  .eyebrow.adviceEyebrow { color: #FDE68A; }
  .eyebrow.adviceEyebrow svg { color: #FDE68A; }

  /* Advice tables & lists */
  .advTable { display: grid; gap: 10px; }
  .advRow { display: grid; gap: 24px; align-items: center; padding: 16px 26px; border-radius: 22px; background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border}; }
  .advHead { display: grid; gap: 24px; padding: 0 26px; font-size: 16px; text-transform: uppercase; letter-spacing: 1.4px; color: rgba(245,245,247,0.5); }
  .advRow b { font-size: 22px; font-weight: 600; display: flex; align-items: center; gap: 12px; }
  .advRow b svg { color: ${({ theme }) => theme.accent}; flex: none; }
  .advRow span { font-size: 19px; line-height: 1.45; color: ${({ theme }) => theme.subText}; }
  .advRow .tools { color: #fff; }
  .pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px; font-size: 17px; font-weight: 600; white-space: nowrap; }
  .pill.buy { background: rgba(59,130,246,0.18); color: #93C5FD; border: 1px solid rgba(59,130,246,0.45); }
  .pill.build { background: ${({ theme }) => `${theme.accent}26`}; color: #C4B5FD; border: 1px solid ${({ theme }) => `${theme.accent}80`}; }
  .pill.now { background: rgba(134,239,172,0.12); color: #86EFAC; border: 1px solid rgba(134,239,172,0.4); }
  .pill.next { background: rgba(252,211,77,0.10); color: #FCD34D; border: 1px solid rgba(252,211,77,0.4); }
  .pill.later { background: rgba(252,165,165,0.10); color: #FCA5A5; border: 1px solid rgba(252,165,165,0.4); }
  .colTitle { font-size: 26px; font-weight: 600; display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
  .winNum { font-size: 44px; font-weight: 800; color: #FDE68A; line-height: 1; }

  .postTag {
    position: absolute !important; top: 56px; right: 140px; z-index: 2;
    display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px;
    font-size: 14px; letter-spacing: 0.6px;
    color: rgba(245,245,247,0.55); border: 1px solid rgba(255,255,255,0.14);
  }

  .card.payoff { background: linear-gradient(180deg, rgba(134,239,172,0.08), rgba(134,239,172,0.02)); border-color: rgba(134,239,172,0.3); }
  .card.payoff .check svg { color: #86EFAC; }

  .painCard { padding: 26px 30px; display: flex; flex-direction: column; gap: 12px; }
  .painCard.off { opacity: 0.45; }
  .painTop { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  .painCard .pill:not(.next) { color: rgba(245,245,247,0.6); border: 1px solid rgba(255,255,255,0.2); }
  .painGaps { font-size: 17px; color: #FDE68A; line-height: 1.45; }
  .painRow { font-size: 18px; line-height: 1.45; color: rgba(245,245,247,0.85); display: grid; grid-template-columns: 190px 1fr; gap: 12px; }
  .painRow span { font-size: 13px; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,245,247,0.5); padding-top: 4px; }
  .painRow.good span { color: #86EFAC; }
  .trackRow { display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; white-space: nowrap; }
  .trackRow .badge { padding: 8px 14px; font-size: 16px; gap: 8px; }
  .ticker { font-size: 15px; color: rgba(245,245,247,0.6); margin-left: 4px; }

  .baseline { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 26px; }
  .baseline .badge { padding: 9px 16px; font-size: 18px; gap: 8px; }
  .baseline .badge svg { color: ${({ theme }) => theme.accent}; }

  /* Data map */
  .dataMap { padding: 26px 30px; display: flex; flex-direction: column; gap: 10px; }
  .dmHead { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }
  .dmHead b { font-size: 24px; font-weight: 600; }
  .dmHead span { font-size: 16px; color: #C4B5FD; }
  .dmStage { display: grid; grid-template-columns: 170px 1fr; gap: 16px; align-items: start; padding: 14px 18px; border-radius: 18px; background: rgba(0,0,0,0.3); border: 1px solid ${({ theme }) => theme.border}; }
  .dmLabel { display: flex; align-items: center; gap: 10px; font-size: 19px; font-weight: 600; padding-top: 2px; }
  .dmLabel svg { color: ${({ theme }) => theme.accent}; }
  .dmRows { display: grid; gap: 8px; }
  .dmRow { display: grid; grid-template-columns: 200px 1fr; gap: 12px; align-items: center; font-size: 17px; }
  .dmRow > span:first-child { color: ${({ theme }) => theme.subText}; }
  .dmChips { display: flex; flex-wrap: wrap; gap: 6px; }
  .dmChip { padding: 3px 10px; border-radius: 999px; font-size: 15px; background: ${({ theme }) => `${theme.accent}33`}; border: 1px solid ${({ theme }) => `${theme.accent}80`}; }
  .dmEmpty { font-size: 15px; color: rgba(245,245,247,0.35); font-style: italic; }
  .dmArrow { text-align: center; color: rgba(196,181,253,0.6); font-size: 18px; line-height: 1; }
  .dmFoot { display: flex; gap: 28px; padding-top: 8px; font-size: 17px; color: ${({ theme }) => theme.subText}; }
  .dmFoot b { color: #fff; font-weight: 600; }

  .missionStats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 56px; max-width: 1500px; }
  .missionStats div { display: flex; flex-direction: column; gap: 6px; padding-top: 18px; border-top: 2px solid ${({ theme }) => `${theme.accent}99`}; }
  .missionStats b { font-size: 40px; font-weight: 700; line-height: 1.1; }
  .missionStats span { font-size: 20px; color: ${({ theme }) => theme.subText}; }
  .orgAsk { margin-top: auto; padding-top: 16px; border-top: 1px solid ${({ theme }) => theme.border}; font-size: 18px; line-height: 1.5; color: rgba(245,245,247,0.85); display: flex; flex-direction: column; gap: 6px; }
  .summaryOpenList { font-size: 17px; color: rgba(245,245,247,0.5); line-height: 1.6; }

  /* Results */
  .resultHero { padding: 30px 34px; display: flex; flex-direction: column; gap: 22px; }
  .resultHeroTop { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
  .resultBig { font-size: 64px; font-weight: 800; color: ${({ theme }) => theme.accent}; line-height: 1; letter-spacing: -1px; margin-top: 6px; }
  .resultHeroSub { font-size: 18px; color: ${({ theme }) => theme.subText}; max-width: 340px; text-align: right; line-height: 1.4; }
  .closeTrack { display: grid; gap: 12px; }
  .ctRow { display: grid; grid-template-columns: 90px 1fr; gap: 16px; align-items: center; }
  .ctLabel { font-size: 16px; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,245,247,0.55); }
  .ctBar { position: relative; height: 46px; border-radius: 12px; background: rgba(255,255,255,0.04); display: flex; align-items: center; }
  .ctFill { height: 100%; border-radius: 12px; display: flex; align-items: center; justify-content: flex-end; padding: 0 16px; }
  .ctFill b { font-size: 19px; font-weight: 700; color: #fff; white-space: nowrap; }
  .ctFill.before { background: rgba(255,255,255,0.18); }
  .ctFill.after { background: ${({ theme }) => theme.accent}; }
  .ctGain { margin-left: 18px; font-size: 18px; font-weight: 600; color: #86EFAC; white-space: nowrap; }
  .ctScale { display: grid; grid-template-columns: repeat(16, 1fr); margin-left: 106px; }
  .ctScale span { font-size: 14px; color: rgba(245,245,247,0.35); text-align: right; padding-right: 4px; font-variant-numeric: tabular-nums; }
  .ctScale span.on { color: #fff; font-weight: 700; }
  .baCard { padding: 22px 26px; display: flex; flex-direction: column; gap: 8px; }
  .baFrom { font-size: 20px; color: rgba(245,245,247,0.5); text-decoration: line-through; text-decoration-color: rgba(245,245,247,0.35); }
  .baTo { font-size: 26px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
  .baTo svg { color: ${({ theme }) => theme.accent}; flex: none; }
  .resultLink { margin-top: auto; padding-top: 18px; border-top: 1px solid ${({ theme }) => theme.border}; font-size: 19px; line-height: 1.5; color: rgba(245,245,247,0.85); display: flex; flex-direction: column; gap: 6px; }

  /* Finance today */
  .proofGrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .proof { display: flex; flex-direction: column; gap: 2px; padding: 12px 16px; border-radius: 18px; background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border}; }
  .proof b { font-size: 25px; font-weight: 800; color: ${({ theme }) => theme.accent}; line-height: 1.1; }
  .proof span { font-size: 15px; color: ${({ theme }) => theme.subText}; line-height: 1.35; }
  .stages { display: grid; gap: 6px; }
  .stage {
    display: grid; grid-template-columns: 170px 1fr; gap: 12px; align-items: center; text-align: left; cursor: pointer;
    padding: 9px 16px; border-radius: 16px; color: #fff;
    background: rgba(255,255,255,0.03); border: 1px solid ${({ theme }) => theme.border};
    &:hover { background: rgba(255,255,255,0.07); }
  }
  .stage.on { background: ${({ theme }) => `${theme.accent}26`}; border-color: ${({ theme }) => theme.accent}; }
  .stageName { font-size: 18px; font-weight: 600; display: flex; flex-direction: column; gap: 2px; }
  .stageName em { font-style: normal; font-size: 13px; text-transform: uppercase; letter-spacing: 1.2px; color: #C4B5FD; font-weight: 600; }
  .stageDesc { font-size: 15px; color: ${({ theme }) => theme.subText}; line-height: 1.35; }
  .painList { display: grid; gap: 12px; }
  .painTheme { border-radius: 24px; background: ${({ theme }) => theme.card}; border: 1px solid ${({ theme }) => theme.border}; overflow: hidden; transition: border-color 0.2s; }
  .painTheme.open { border-color: ${({ theme }) => `${theme.accent}80`}; }
  .painTheme.off { opacity: 0.5; }
  .painHead { all: unset; box-sizing: border-box; width: 100%; cursor: pointer; display: grid; grid-template-columns: 48px 1fr auto 24px; gap: 18px; align-items: center; padding: 18px 24px; }
  .painHead:hover { background: rgba(255,255,255,0.03); }
  .painHeadText h3 { font-size: 24px; margin: 0 0 4px; }
  .painList.hasOpen .painTheme:not(.open) .painHeadText span { display: none; }
  .painList.hasOpen .painTheme:not(.open) .painHead { padding: 12px 24px; }
  .painHeadText span { font-size: 16px; color: ${({ theme }) => theme.subText}; line-height: 1.4; }
  .painTheme .pill:not(.next) { color: rgba(245,245,247,0.6); border: 1px solid rgba(255,255,255,0.2); }
  .painChevron { color: rgba(245,245,247,0.55); transition: transform 0.2s; }
  .painTheme.open .painChevron { transform: rotate(180deg); color: #C4B5FD; }
  .painBody { display: grid; gap: 8px; padding: 0 24px 18px 90px; }
  .painBody .painRow { margin-top: 6px; font-size: 16px; }
  .gapRow { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 8px 16px; border-radius: 14px; background: rgba(252,211,77,0.05); border: 1px solid rgba(252,211,77,0.22); cursor: pointer; }
  .gapRow > div { display: flex; flex-direction: column; gap: 2px; }
  .gapRow b { font-size: 17px; font-weight: 600; }
  .gapRow > div > span { font-size: 14px; color: ${({ theme }) => theme.subText}; line-height: 1.4; }
  .gapRow:hover { background: rgba(252,211,77,0.1); }
  .gapRow.off { background: rgba(255,255,255,0.02); border-color: ${({ theme }) => theme.border}; opacity: 0.55; }
  .gapRow.off b { text-decoration: line-through; text-decoration-color: rgba(245,245,247,0.5); }
  .gapRow .gapState { white-space: nowrap; }
  .gapRow.off .gapState { color: rgba(245,245,247,0.55); }
  .gapGrid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
  .gap {
    display: flex; flex-direction: column; gap: 3px; padding: 11px 16px; border-radius: 16px;
    background: rgba(252,211,77,0.05); border: 1px solid rgba(252,211,77,0.32);
    transition: opacity 0.2s, background 0.2s, border-color 0.2s;
  }
  .gap b { font-size: 18px; font-weight: 600; }
  .gap > span { font-size: 15px; color: ${({ theme }) => theme.subText}; line-height: 1.4; }
  .gap.off { background: rgba(255,255,255,0.02); border-color: ${({ theme }) => theme.border}; opacity: 0.5; }
  .gap.off b { text-decoration: line-through; text-decoration-color: rgba(245,245,247,0.5); }
  .gap.selectable:hover { background: rgba(252,211,77,0.09); }
  .gap.off.selectable:hover { background: rgba(255,255,255,0.05); }
  .gapTop { display: flex; justify-content: space-between; align-items: center; }
  .gapTag { font-size: 12px; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,245,247,0.55); }
  .gapState { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #FCD34D; }
  .gap.off .gapState { color: rgba(245,245,247,0.55); }

  /* --- Footer --- */
  .footer {
    position: absolute; left: 140px; right: 140px; bottom: 52px;
    display: flex; justify-content: space-between; align-items: center;
    font-size: 15px; color: rgba(245,245,247,0.42); font-variant-numeric: tabular-nums;
    .brand { font-size: 18px; gap: 10px; color: ${({ theme }) => theme.subText}; }
  }
`;

/* ---------- Deck chrome ---------- */

export const ProgressBar = styled.div`
  position: fixed; left: 0; top: 0; height: 3px; z-index: 11;
  background: ${({ theme }) => theme.accent};
  transition: width 0.35s ease;
  @media print { display: none; }
`;

export const Chrome = styled.div`
  position: fixed; left: 50%; bottom: 12px; transform: translateX(-50%); z-index: 10;
  display: flex; align-items: center; gap: 2px; padding: 3px 6px;
  background: rgba(10,10,14,0.72); backdrop-filter: blur(14px);
  border: 1px solid rgba(255,255,255,0.08); border-radius: 999px;
  opacity: 0.45; transition: opacity 0.25s;
  font-size: 12px;

  &:hover, &:focus-within { opacity: 1; }

  button {
    border: none; background: transparent; cursor: pointer;
    color: rgba(245,245,247,0.7); font-size: 12px; line-height: 1;
    padding: 6px 9px; border-radius: 999px;
    display: inline-flex; align-items: center; gap: 6px;
    &:hover { background: rgba(255,255,255,0.08); color: #fff; }
  }
  button.on { background: ${({ theme }) => `${theme.accent}cc`}; color: #fff; }
  .count { font-size: 12px; color: rgba(245,245,247,0.7); padding: 0 2px; min-width: 34px; text-align: center; font-variant-numeric: tabular-nums; }
  .count.answered { display: inline-flex; align-items: center; gap: 5px; color: rgba(245,245,247,0.6); padding: 0 6px; min-width: 0; }
  .sep { width: 1px; height: 14px; background: rgba(255,255,255,0.12); margin: 0 4px; }
  .saveStatus svg { width: 11px; height: 11px; }

  @media print { display: none; }
`;

/* ---------- Password gate (styled after the site's contact FormCard) ---------- */

export const Gate = styled.div`
  position: fixed; inset: 0; z-index: 100;
  display: flex; align-items: center; justify-content: center;
  padding: 24px; overflow: auto;
  background: ${({ theme }) => theme.bg};

  .heroBg { position: absolute; inset: 0; pointer-events: none; background: ${heroBg(800, 360)}; }
  .wrap { position: relative; width: 100%; max-width: 460px; }
  .gateBrand { display: flex; align-items: center; gap: 8px; font-weight: 700; letter-spacing: 0.2px; margin-bottom: 28px; }

  .formCard {
    background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05));
    border: 1px solid ${({ theme }) => theme.border};
    backdrop-filter: blur(12px);
    border-radius: 28px; padding: 34px;
    box-shadow: 0 40px 80px rgba(0,0,0,0.45);
  }
  .gateEyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 13px; text-transform: uppercase; letter-spacing: 1.1px;
    color: ${({ theme }) => theme.subText}; margin-bottom: 14px;
    svg { color: ${({ theme }) => theme.accent}; }
  }
  h1 { font-size: 28px; font-weight: 600; line-height: 1.25; margin: 0 0 10px; }
  .gateTitle { display: flex; align-items: center; gap: 12px; }
  .clientLogo { display: block; flex: none; object-fit: contain; border-radius: 6px; background: #fff; padding: 3px; }
  p { font-size: 15px; line-height: 1.7; color: ${({ theme }) => theme.subText}; margin: 0 0 22px; }
  label { display: block; font-size: 13px; color: rgba(245,245,247,0.72); margin-bottom: 6px; }
  input {
    width: 100%;
    background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.16);
    border-radius: 14px; padding: 12px 14px;
    color: #fff; font-size: 15px; line-height: 1.5;
    &::placeholder { color: rgba(245,245,247,0.45); }
    &:focus { outline: none; border-color: rgba(255,255,255,0.32); }
  }
  button {
    margin-top: 14px; width: 100%;
    background: #fff; color: #000; border: none; border-radius: 999px;
    padding: 14px 16px; font-size: 15px; font-weight: 850; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  }
  .err { margin-top: 12px; font-size: 13px; min-height: 18px; color: rgba(252,165,165,0.95); }
  .foot { margin-top: 18px; font-size: 12px; text-align: center; color: rgba(245,245,247,0.52); }
`;
